import type { ContentRegistry } from '../content/registry';
import type { InputFrame } from '../core/input';
import { dcos, dsin } from '../core/dmath';
import { wrapCoord, wrapDelta, type PlayerState } from './world';

/**
 * In-level portals (roadmap #4). After a stage or era clears and its level-ups
 * resolve, destination portals open on the map; players walk into one to
 * travel. Everything here is deterministic sim state driven only by player
 * positions and input frames, so replays and lockstep co-op stay in sync
 * without a separate "continue" command.
 *
 * Commit rule: a portal holding a strict majority of active players starts a
 * countdown (1 s solo, 3 s co-op); anyone can still switch and reset it.
 * Players with no input for IDLE_EXCLUDE_SEC don't count toward the majority,
 * so an idle or disconnected teammate never blocks travel.
 */

export type PortalKind = 'stage' | 'stable' | 'fractured' | 'volatile';

export interface PortalBenefit {
  title: string;
  detail: string;
  healFraction: number;
  bonusShards: number;
  bonusPicks: number;
  bonusRerolls: number;
}

/** Exact, previewable reward for each route. Shortcut danger still comes from destination scaling. */
export const PORTAL_BENEFITS: Record<PortalKind, PortalBenefit> = {
  stage: { title: 'Continuity Restored', detail: 'Recover 15% health', healFraction: 0.15, bonusShards: 0, bonusPicks: 0, bonusRerolls: 0 },
  stable: { title: "Anchor's Grace", detail: 'Recover 35% health · +1 reroll', healFraction: 0.35, bonusShards: 0, bonusPicks: 0, bonusRerolls: 1 },
  fractured: { title: 'Fractured Cache', detail: '◆ 25 · +1 upgrade pick', healFraction: 0.15, bonusShards: 25, bonusPicks: 1, bonusRerolls: 0 },
  volatile: { title: 'Paradox Cache', detail: '◆ 60 · +2 upgrade picks', healFraction: 0, bonusShards: 60, bonusPicks: 2, bonusRerolls: 0 },
};

export interface PortalState {
  /** Destination era index (for 'stage', the current era). */
  era: number;
  kind: PortalKind;
  x: number;
  y: number;
}

export const PORTAL_RADIUS = 1.6;
export const PORTAL_DISTANCE = 7;
export const SOLO_COMMIT_SEC = 1;
export const COOP_COMMIT_SEC = 3;
export const IDLE_EXCLUDE_SEC = 10;
/** Angular spacing between era portals on their arc (radians). */
const ARC_STEP = 0.85;

/** Where the party can go next: one 'stage' portal mid-era, or the next era plus up to two riskier skips. */
export function portalDestinations(reg: ContentRegistry, eraIndex: number, eraCleared: boolean): { era: number; kind: PortalKind }[] {
  if (!eraCleared) return [{ era: eraIndex, kind: 'stage' }];
  const kinds: PortalKind[] = ['stable', 'fractured', 'volatile'];
  return reg.eras
    .filter((e) => e.implemented && e.index > eraIndex && e.index <= eraIndex + 3)
    .map((e) => ({ era: e.index, kind: kinds[Math.min(2, e.index - eraIndex - 1)] }));
}

/**
 * Lay portals on an arc "ahead" of (cx, cy): the stable route in the middle,
 * shortcuts to either side. Positions wrap like everything else in the world.
 */
export function placePortals(dests: { era: number; kind: PortalKind }[], cx: number, cy: number, half: number): PortalState[] {
  const order = dests.length === 3 ? [1, 0, 2] : dests.length === 2 ? [0, 1] : [0];
  const base = -Math.PI / 2;
  const start = base - (ARC_STEP * (dests.length - 1)) / 2;
  const out: PortalState[] = [];
  order.forEach((destIndex, slot) => {
    const a = start + ARC_STEP * slot;
    const d = dests[destIndex];
    if (!d) return;
    out.push({ era: d.era, kind: d.kind, x: wrapCoord(cx + dcos(a) * PORTAL_DISTANCE, half), y: wrapCoord(cy + dsin(a) * PORTAL_DISTANCE, half) });
  });
  // Stable route first in the array, so index 0 is always "normal progression".
  return out.sort((p, q) => kindRank(p.kind) - kindRank(q.kind));
}

function kindRank(kind: PortalKind): number {
  return kind === 'stage' || kind === 'stable' ? 0 : kind === 'fractured' ? 1 : 2;
}

/** Index of the portal the player stands in, or -1. */
export function portalAt(portals: readonly PortalState[], x: number, y: number, half: number): number {
  for (let i = 0; i < portals.length; i++) {
    const dx = wrapDelta(portals[i].x - x, half);
    const dy = wrapDelta(portals[i].y - y, half);
    if (dx * dx + dy * dy <= PORTAL_RADIUS * PORTAL_RADIUS) return i;
  }
  return -1;
}

export interface PortalTally {
  /** Portal each player stands in (-1 = none), by player index. */
  occupancy: number[];
  votes: number[];
  /** Players needed in one portal to start the countdown. */
  required: number;
  /** Portal with a winning majority, or -1. */
  leader: number;
}

export function tallyPortals(portals: readonly PortalState[], players: readonly PlayerState[], idleSec: readonly number[], half: number): PortalTally {
  const votes = portals.map(() => 0);
  const occupancy: number[] = [];
  let active = 0;
  for (const p of players) {
    if (!p.alive) { occupancy[p.index] = -1; continue; }
    if ((idleSec[p.index] ?? 0) < IDLE_EXCLUDE_SEC) active++;
    const at = portalAt(portals, p.x, p.y, half);
    occupancy[p.index] = at;
    if (at >= 0) votes[at]++;
  }
  // Idle players still count as votes when standing in a portal, but never as blockers.
  const required = Math.floor(Math.max(1, active) / 2) + 1;
  let leader = -1;
  for (let i = 0; i < votes.length; i++) if (votes[i] >= required && (leader < 0 || votes[i] > votes[leader])) leader = i;
  return { occupancy, votes, required, leader };
}

export function commitSeconds(playerCount: number): number {
  return playerCount > 1 ? COOP_COMMIT_SEC : SOLO_COMMIT_SEC;
}

export function isActiveInput(f: InputFrame): boolean {
  return f.mx !== 0 || f.my !== 0 || f.dash || f.action;
}
